import useEvents from './hooks/useEvents';
import { useEffect, useState } from 'react';
import { Heading } from '@/src/tailwindui/heading';
import { Button } from '@/src/tailwindui/button';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon } from 'lucide-react';
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid';
import { Input, InputGroup } from '@/src/tailwindui/input';

import {
    useReactTable,
    createColumnHelper,
    getCoreRowModel,
    SortingState,
    getSortedRowModel,
    ColumnFiltersState,
    FilterFn,
    getFilteredRowModel,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
    getPaginationRowModel,
} from '@tanstack/react-table';
import { rankItem } from '@tanstack/match-sorter-utils';
import SmallLoader from '@/src/Common/components/SmallLoader';
import EventRow from './EventRow';
import {
    Pagination,
    PaginationGap,
    PaginationList,
    PaginationNext,
    PaginationPage,
    PaginationPrevious,
} from '@/src/tailwindui/pagination';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

declare module '@tanstack/table-core' {
    interface FilterFns {
        fuzzy: FilterFn<unknown>;
    }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);

    addMeta({
        itemRank,
    });

    return itemRank.passed;
};

const columnHelper = createColumnHelper<Activation>();

export default function EventsList() {
    const { events, isLoading, getEvents } = useEvents();
    const navigate = useNavigate();
    const [filteredEvents, setFilteredEvents] = useState(events);
    const [search, setSearch] = useState('');
    const { orgId } = useUserOrg();

    useEffect(() => {
        getEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    const columnDefinitions = [
        columnHelper.accessor('id', {
            id: 'id',
            header: () => <span>id</span>,
            cell: (info) => (
                <span>
                    <Link to={`${info.row.original.id}`}>
                        {info.getValue()}
                    </Link>
                </span>
            ),
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: () => <span>name</span>,
            cell: (info) => (
                <span>
                    <Link to={`${info.row.original.id}`}>
                        {info.getValue()}
                    </Link>
                </span>
            ),
        }),
        columnHelper.accessor('date', {
            id: 'date',
            header: () => <span>date</span>,
            cell: (info) => <span>{info.getValue().split('T')[0]}</span>,
        }),
        columnHelper.accessor('location', {
            id: 'location',
            header: () => <span>location</span>,
            cell: (info) => <span>{info.getValue()}</span>,
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props: { row: any }) => (
                /*<ActivationActions onDelete={onDelete} row={props.row} />*/
                <div>actions</div>
            ),
        }),
    ];

    useEffect(() => {
        setFilteredEvents(
            events.filter(
                (event: any) =>
                    !event.isArchived &&
                    event.name.toLowerCase().includes(search.toLowerCase()),
            ),
        );
    }, [events, search]);

    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [sorting, setSorting] = useState<SortingState>([
        { id: 'date', desc: true },
    ]);
    let hasGap = false;
    let wasLastRenderedGap = false;
    const options = {
        data: filteredEvents,
        columns: columnDefinitions,
        filterFns: {
            fuzzy: fuzzyFilter,
        },
        state: {
            columnFilters,
            sorting,
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        getCoreRowModel: getCoreRowModel(),
    };

    // @ts-ignore
    options.getPaginationRowModel = getPaginationRowModel();

    // @ts-ignore
    const table = useReactTable(options);

    if (isLoading) {
        return (
            <div className="text-center text-zinc-500 dark:text-zinc-400 w-full h-[calc(100vh-200px)] flex flex-col items-center justify-center">
                <SmallLoader />
            </div>
        );
    }

    if (events.length === 0) {
        return (
            <div className="text-center text-zinc-500 dark:text-zinc-400 w-full h-[calc(100vh-200px)] flex flex-col items-center justify-center">
                Looks like you haven't created an event yet!
                <Button
                    className="light-button px-4 py-2 mt-6"
                    onClick={() => {
                        navigate('/events/create');
                    }}
                >
                    Create Your First Event Now
                </Button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-4xl">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading className="text-4xl plex-sans">Events</Heading>
                    <div className="mt-4 flex max-w-xl gap-4">
                        <div className="flex-1 search-input-wrapper">
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    name="search"
                                    placeholder="Search events&hellip;"
                                    className="search-input monospace-font"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </InputGroup>
                        </div>
                    </div>
                </div>
                <Button
                    className="-my-0.5 dark-button"
                    onClick={() => {
                        navigate('/events/create');
                    }}
                >
                    <PlusIcon className="size-4" />
                    <span className="ml-2">Create Event</span>
                </Button>
            </div>
            <ul className="mt-10">
                {isLoading ? (
                    <SmallLoader />
                ) : (
                    table.getRowModel().rows.map((object: any, index: any) => {
                        const event = object.original;

                        return <EventRow event={event} index={index} />;
                    })
                )}
                {filteredEvents.length === 0 && events.length > 0 && (
                    <div className="text-center text-zinc-500 dark:text-zinc-400">
                        <MagnifyingGlassIcon className="size-14 mx-auto" />
                        <br />
                        Your Search Didn't Return Any Results, clear the filters
                        and try again!
                    </div>
                )}
            </ul>
            {filteredEvents.length > 0 && table.getPageOptions().length > 1 && (
                <div className="mt-10 max-w-xl mx-auto">
                    <Pagination>
                        <PaginationPrevious
                            className={'text-zinc-500 dark:text-white'}
                            onClick={() => table.previousPage()}
                        />

                        <PaginationList>
                            {table.getPageOptions().map((page) => {
                                const currentPage =
                                    table.getState().pagination.pageIndex;
                                const pageCount = table.getPageOptions().length;

                                const isLastPages =
                                    page === pageCount - 1 ||
                                    page === pageCount - 2;

                                const isFirstPages = page === 0 || page === 1;
                                const isSecondToLastPage =
                                    page === pageCount - 2;

                                if (
                                    (page > currentPage + 1 ||
                                        page < currentPage - 1) &&
                                    !isFirstPages &&
                                    !isLastPages
                                ) {
                                    if (!hasGap && !wasLastRenderedGap) {
                                        hasGap = true;
                                        wasLastRenderedGap = true;
                                        return (
                                            <PaginationGap
                                                key={`gap-${page}`}
                                            />
                                        );
                                    }
                                    return null;
                                }
                                wasLastRenderedGap = !isSecondToLastPage;

                                const pageIndex = page;
                                return (
                                    <>
                                        {isSecondToLastPage && (
                                            <PaginationGap
                                                key={`gap-${page}`}
                                                className={
                                                    'text-zinc-500 dark:text-white'
                                                }
                                            />
                                        )}
                                        <PaginationPage
                                            key={pageIndex}
                                            onClick={() =>
                                                table.setPageIndex(pageIndex)
                                            }
                                            current={
                                                pageIndex ===
                                                table.getState().pagination
                                                    .pageIndex
                                            }
                                        >
                                            <span className="text-zinc-500 dark:text-white">
                                                {pageIndex + 1}
                                            </span>
                                        </PaginationPage>
                                    </>
                                );
                            })}
                        </PaginationList>
                        <PaginationNext
                            className={'text-zinc-500 dark:text-white'}
                            onClick={() => table.nextPage()}
                        />
                    </Pagination>
                </div>
            )}
        </div>
    );
}
